import network from '@/services/network';
import {
  AddPoolMemberRequest,
  ApiResponse,
  BookingRule,
  BookingRulePath,
  BookingRuleWerte,
  CreateMeetingPoolRequest,
  MeetingPool,
  PaginatedResponse,
  UpdateMeetingPoolRequest,
  UpdatePoolMemberRequest,
} from '@/types';
import { ErrorResponse } from '@/utils';

const getMeetingPools = async (params = {}): Promise<PaginatedResponse<MeetingPool>> => {
  try {
    const { data } = await network.get<PaginatedResponse<MeetingPool>>(
      '/meeting-pools',
      { params },
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const getMeetingPool = async (id: number): Promise<ApiResponse<MeetingPool>> => {
  try {
    const { data } = await network.get<ApiResponse<MeetingPool>>(
      `/meeting-pools/${id}`,
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const createMeetingPool = async (poolData: CreateMeetingPoolRequest): Promise<ApiResponse<MeetingPool>> => {
  try {
    const { data } = await network.post<ApiResponse<MeetingPool>>(
      '/meeting-pools',
      poolData,
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

export interface CreateTopicPoolRequest {
  name: string;
  slot_duration_minutes?: number | null;
}

/**
 * Themenpool-Umbau: legt Pool + meeting_topic + Kopplung atomar an.
 * name ist gleichzeitig Pool-Name und Themen-Name; slug wird vom Backend
 * abgeleitet (Str::slug + Suffix bei Kollision).
 */
const createTopicPool = async (
  poolData: CreateTopicPoolRequest,
): Promise<ApiResponse<MeetingPool>> => {
  try {
    const { data } = await network.post<ApiResponse<MeetingPool>>(
      '/meeting-pools/topic-pool',
      poolData,
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const updateMeetingPool = async (
  id: number,
  poolData: UpdateMeetingPoolRequest,
): Promise<ApiResponse<MeetingPool>> => {
  try {
    const { data } = await network.put<ApiResponse<MeetingPool>>(
      `/meeting-pools/${id}`,
      poolData,
    );
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

/**
 * Globale Buchungsregeln je Weg (Slot-Modell, Entscheid Alex 12.08.2026,
 * docs/tasks/pool-dauer-puffer.md §N.4). Bewusst nur lesen und aendern: die
 * Zeilen entstehen in der Migration und werden nie angelegt oder geloescht.
 */
const getBookingRules = async (): Promise<{ data: BookingRule[] }> => {
  try {
    const { data } = await network.get<{ data: BookingRule[] }>('/booking-rules');
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const updateBookingRule = async (
  path: BookingRulePath,
  werte: BookingRuleWerte,
): Promise<{ data: BookingRule }> => {
  try {
    const { data } = await network.put<{ data: BookingRule }>(`/booking-rules/${path}`, werte);
    return data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const deleteMeetingPool = async (id: number): Promise<void> => {
  try {
    await network.delete(`/meeting-pools/${id}`);
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const addPoolMember = async (poolId: number, data: AddPoolMemberRequest): Promise<ApiResponse<MeetingPool>> => {
  try {
    const response = await network.post<ApiResponse<MeetingPool>>(
      `/meeting-pools/${poolId}/members`,
      data,
    );
    return response.data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const updatePoolMember = async (
  poolId: number,
  userId: number,
  data: UpdatePoolMemberRequest,
): Promise<ApiResponse<MeetingPool>> => {
  try {
    const response = await network.put<ApiResponse<MeetingPool>>(
      `/meeting-pools/${poolId}/members/${userId}`,
      data,
    );
    return response.data;
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const removePoolMember = async (poolId: number, userId: number): Promise<void> => {
  try {
    await network.delete(`/meeting-pools/${poolId}/members/${userId}`);
  } catch (error) {
    throw new ErrorResponse(error);
  }
};

const meetingPoolApi = {
  getMeetingPools,
  getMeetingPool,
  createMeetingPool,
  createTopicPool,
  updateMeetingPool,
  getBookingRules,
  updateBookingRule,
  deleteMeetingPool,
  addPoolMember,
  updatePoolMember,
  removePoolMember,
};

export default meetingPoolApi;
